import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from "@mui/material";
import { Box } from "@mui/system";
import React, { useImperativeHandle, useState, ReactElement, useEffect, useRef } from "react";

import { visuallyHidden } from '@mui/utils';
import Pagination from "./Pagination";
import Selector from "./Selector";
import Loading from "../Loading";
import { formatCurrency } from "../../../lib/helpers";
import { getCurrencySymbolByCurrencyId } from "../../../common/helpers/getCurrencySymbolByCurrencyId";

function descendingComparator(a: any, b: any, orderBy: any) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(
    order: any,
    orderBy: any,
) {
    return order === 'desc'
        ? (a: any, b: any) => descendingComparator(a, b, orderBy)
        : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

function stableSort(array: Array<any>, comparator: any) {
    if (!Array.isArray(array)) {
        // Bir dizi değilse boş bir dizi döndür ve bir hata fırlat
        console.error("stableSort bir dizi almalıdır.");
        return [];
    }

    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export interface HeadCellType {
    id?: any;
    isprimary?: boolean;
    hide?: boolean;
    className?: string;
    bodyClasName?: string;
    label?: string;
    isMoney?: boolean;
    forcealign?: "right" | "left" | "center" | "inherit" | "justify" | undefined | string;
    align?: "right" | "left" | "center" | "inherit" | "justify" | undefined;
    disableFormat?: boolean;
    isRender?: boolean;
    renderRow?: any;
    hideSortIcon?: boolean,
    numeric?: boolean,
    exceptToExcel?: boolean,
    colWidth?: number,
    maxLength?: number,
    currencySymbolCol?: string,
    disabledTooltip?: boolean,
    disableSortIcon?: boolean,
    showTotal?: boolean;
}
type TableProps = {
    tableData?: any,
    headerCells: Array<HeadCellType>,
    isRowSelect?: boolean,
    bottomElement?: ReactElement,
    emptyMessage?: string,
    isLoading?: boolean,
    tableStyle?: any,
    isDisablePaging?: boolean,
    disableShadow?: boolean,
    className?: string,
    initialPageSize?: number,
    totalParameter?: string | any,
    totalLabel?: string,
    hideToolTip?: boolean,
    defaultSelected?: any,
    secondTotalParamater?: string | any,
    secondTotalLabel?: string,
    showTotalRow?: boolean,
    totalAmountCurrency?: number,

    // Pagination için
    onPageChange?: (page: number) => void;
    onRowsPerPageChange?: (rows: number) => void;
    totalDataCount?: number;
    currentPage?: number;
}


const MaterailTable = React.forwardRef((props: TableProps, ref) => {

    const { tableData, headerCells, isRowSelect, bottomElement, emptyMessage, isLoading,
        tableStyle, isDisablePaging, disableShadow, className, totalParameter, totalLabel, hideToolTip, defaultSelected,
        secondTotalParamater, secondTotalLabel, showTotalRow, totalDataCount, currentPage, totalAmountCurrency } = props

    useImperativeHandle(ref, () => ({

        getSelectedRows() {
            return selected;
        },
        clearSelected() {
            setSelected([]);
        },
        resetPage() {
            setPage(1);
        }

    }));

    var primaryRowIndex = headerCells.findIndex(w => w.isprimary == true);;
    var primaryRow = headerCells[primaryRowIndex]?.id;

    const isInitial = useRef(true);

    useEffect(() => {

        if (isInitial.current) {
            isInitial.current = false;
        }

        if (currentPage != null && currentPage !== undefined) {
            // pagination varsa
            setPage(currentPage);
        } else {
            // pagination yoksa
            setPage(1);
        }
    }, [tableData]);

    useEffect(() => {
        if (defaultSelected) {
            setSelected(defaultSelected);
        }
    }, []);

    var rowPerPage = isDisablePaging ? 1000000 : (props.initialPageSize ? props.initialPageSize : 10);


    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(rowPerPage);


    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('calories');
    const [selected, setSelected] = useState<any>([]);
    const [dense, setDense] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(rowPerPage);

    const handleClick = (event: any, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: any = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (newPage: any) => {
        setPage(newPage);
        setLimit(newPage);
        props.onPageChange && props.onPageChange(newPage);
    };


    const handleRequestSort = (
        event: any,
        property: any,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: any) => {
        if (event.target.checked) {
            const newSelected = tableData.map((n: any) => n[primaryRow]);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };
    const isSelected = (name: string) => {

        return selected.indexOf(name) !== -1;
    };

    const onLimitChange = (val: any) => {
        setPage(1);
        setRowsPerPage(parseInt(val.value, 10));
        // setLimit(val.value)
        props.onRowsPerPageChange && props.onRowsPerPageChange(parseInt(val.value, 10));
    };

    const calculateTotal = () => {
        const data = tableData.find((item: any) => item.currencySymbol || item.currencyid || item.currencyId);

        const total = tableData.reduce((sum: any, item: any) => sum + (item[totalParameter] || 0), 0);
        const formattedValue = new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2, // En az 2 ondalık basamak göster
            maximumFractionDigits: 2, // En fazla 2 ondalık basamak göster
        }).format(total);

        return `${formattedValue} ${data.currencySymbol || getCurrencySymbolByCurrencyId(data.currencyid)}`;
    }

    const calculateSecondTotal = () => {
        const data = tableData.find((item: any) => item.currencySymbol || item.currencyid || item.currencyId);

        const total = tableData.reduce((sum: any, item: any) => sum + (item[secondTotalParamater] || 0), 0);
        const formattedValue = new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2, // En az 2 ondalık basamak göster
            maximumFractionDigits: 2, // En fazla 2 ondalık basamak göster
        }).format(total);

        return `${formattedValue} ${data.currencySymbol || getCurrencySymbolByCurrencyId(data.currencyid)}`;
    }

    function EnhancedTableHead(props: any) {
        const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
            props;
        const createSortHandler =
            (property: any) => (event: any) => {
                onRequestSort(event, property);
            };

        const headCellFilters = props.headCells.filter(function (el: HeadCellType) {
            return el.hide != true
        });
        return (
            <TableHead>
                <TableRow className="divide-x divide-gray-200" >
                    {isRowSelect ? (
                        <TableCell padding="checkbox">
                            <Checkbox
                                color="primary"
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{
                                    'aria-label': 'select all desserts',
                                }}
                            />
                        </TableCell>
                    ) : null}
                    {headCellFilters.map((headCell: HeadCellType) => (
                        <TableCell
                            key={headCell.id}
                            align={"left"}
                            padding={"normal"}
                            sortDirection={orderBy === headCell.id ? order : false}
                            className={`${headCell.className} `}

                            style={{
                                paddingLeft: 10,
                                paddingRight: 10
                            }}
                        >
                            {headCell.disableSortIcon ?
                                <>
                                    {headCell.label}
                                    {orderBy === headCell.id ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </> :
                                <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : 'asc'}
                                    onClick={createSortHandler(headCell.id)}
                                    hideSortIcon={headCell.hideSortIcon == true ? true : false}
                                >
                                    {headCell.label}
                                    {orderBy === headCell.id ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    }

    const getRowValue = (row: HeadCellType, value: any) => {

        var returnValue = {
            value: value || "",
            tooltipValue: ""
        }
        var val = (value || "") + "";
        if (row.maxLength && row.maxLength > 0) {

            if (val && val.length > row.maxLength) {
                var newVal = val.substring(0, row.maxLength);

                returnValue.value = newVal + "...";

                returnValue.tooltipValue = row.disabledTooltip ? "" : val;
                return returnValue;
            } else return returnValue;

        } else {
            return returnValue;
        }
    }
    if (tableData.length == 0 || isLoading) {

        return (
            <div className={`m-4 p-4 bg-white rounded-md ${disableShadow ? "" : "shadow-md"}`}>
                <div className="w-full flex flex-col">
                    <div className="m-4 p-4 bg- ">
                        <div className="min-w-full py-2 align-middle md:px-2 lg:px-2">
                            <div className="text-center font-semibold py-48 sm:py-16 md:py-24">
                                {isLoading ? (
                                    <Loading desc={""}></Loading>
                                ) : (
                                    emptyMessage || 'Kayıtların listelenmesi için lütfen "Hazırla" butonuna basınız.'
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const calculateColumnTotal = (columnId: string) => {
        return tableData.reduce((sum: number, row: any) => {
            const val = row[columnId];
            return sum + (typeof val === "number" ? val : 0);
        }, 0);
    };

    // Backend pagination mı aktif kontrolü
    const isServerPagination =
        totalDataCount != null &&
        totalDataCount > tableData.length; // backend daha fazla kayıt olduğunu söylüyor mu?

    // Backend paginated ise slice uygulama
    const displayedData = isServerPagination
        ? stableSort(tableData, getComparator(order, orderBy)) // sadece sıralama
        : stableSort(tableData, getComparator(order, orderBy))
            .slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage);


    return (
        <>
            <div className={`mt-3 mb-4 mx-4 p-4 bg-white rounded-md ${disableShadow ? "" : "shadow-md"} ${className}`}>
                <Box sx={{ width: '100%' }}>


                    <Paper sx={{ width: '100%', mb: 2 }}>
                        <TableContainer style={{
                            display: "grid"
                        }} >
                            <Table
                                sx={{ minWidth: 750, marginBottom: "15px" }}
                                aria-labelledby="tableTitle"
                                size={dense ? 'small' : 'medium'}
                                border={1}
                                style={{
                                    ...tableStyle
                                }}

                            >
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={order}
                                    orderBy={orderBy}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={tableData.length}
                                    headCells={headerCells}
                                />
                                <TableBody>

                                    {tableData.length == 0 ? (
                                        <TableRow
                                            hover

                                            tabIndex={-1}
                                            className="divide-x divide-gray-200"

                                        >
                                            <TableCell padding="checkbox" colSpan={headerCells.length + 1} >
                                                <div className="w-full">
                                                    <div className="w-full flex flex-col">
                                                        <div className="m-4 p-4 bg- ">
                                                            <div className="min-w-full py-2 align-middle md:px-2 lg:px-2">
                                                                <div className="text-center font-semibold py-48 sm:py-16 md:py-24">
                                                                    {isLoading ? (
                                                                        <Loading></Loading>
                                                                    ) : (
                                                                        emptyMessage || 'Kayıtların listelenmesi için lütfen "Hazırla" butonuna basınız.'
                                                                    )}

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                    {displayedData.map((row: any, index: number) => {
                                        var primaryValue = row[primaryRow];
                                        const isItemSelected = isSelected(row[primaryRow]);
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        const headCellFilters = headerCells.filter(function (el) {
                                            return el.hide != true
                                        });

                                        return (
                                            <TableRow
                                                hover
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={"TableRow" + index}
                                                selected={isItemSelected}
                                                className="divide-x divide-gray-200"

                                            >
                                                {isRowSelect ? (
                                                    <TableCell padding="checkbox" height={40}>
                                                        <Checkbox
                                                            color="primary"
                                                            onClick={(event) => {
                                                                handleClick(event, primaryValue)
                                                            }}
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': labelId,
                                                            }}
                                                        />
                                                    </TableCell>
                                                ) : (null)}

                                                {
                                                    headCellFilters.map((item, index) => {

                                                        var rowValue = row[item.id];
                                                        item.align = "left";
                                                        let align: any = item.align;


                                                        if (item.isMoney) {
                                                            if (item.disableFormat != true) {
                                                                if (item.currencySymbolCol) {
                                                                    rowValue = formatCurrency(rowValue) + " " + row[item.currencySymbolCol];
                                                                } else {
                                                                    rowValue = formatCurrency(rowValue) + " ₺";
                                                                }
                                                            }
                                                            align = "right"
                                                        }
                                                        if (item.forcealign) {
                                                            align = item.forcealign
                                                        }

                                                        var rowValueWithTooltip = getRowValue(item, rowValue);
                                                        rowValue = rowValueWithTooltip.value;
                                                        var tooltip = rowValueWithTooltip.tooltipValue;



                                                        return (
                                                            <TableCell
                                                                padding="none"
                                                                style={{
                                                                    paddingLeft: 10,
                                                                    paddingRight: 10
                                                                }}
                                                                component="th"
                                                                id={item.id}
                                                                scope="row"
                                                                align={align || "left"}
                                                                className={"whitespace-nowrap !py-2 " + item.bodyClasName}
                                                                title={hideToolTip ? "" : tooltip}
                                                            >
                                                                {item.isRender ? item.renderRow(row) : rowValue}
                                                            </TableCell>
                                                        );
                                                    })
                                                }

                                            </TableRow>
                                        );
                                    })}
                                    {props.showTotalRow ? <TableRow className="divide-x divide-gray-200 bg-gray-100">
                                        {isRowSelect ? <TableCell /> : null}

                                        {headerCells.filter(el => !el.hide).map((item, idx) => {
                                            if (idx === 0) {
                                                return (
                                                    <TableCell
                                                        padding="none"
                                                        style={{
                                                            paddingLeft: 10,
                                                            paddingRight: 10
                                                        }}
                                                        className={"whitespace-nowrap !py-2 " + item.bodyClasName}
                                                        key={`total-label`}
                                                        align="left"
                                                        sx={{ fontWeight: "bold", textDecoration: "" }}
                                                    >
                                                        TOPLAM
                                                    </TableCell>
                                                );
                                            }

                                            let cellValue: any = "";

                                            if (item.showTotal) {
                                                const total = calculateColumnTotal(item.id);

                                                if (item.isMoney) {
                                                    if (item.currencySymbolCol) {
                                                        cellValue =
                                                            formatCurrency(total) +
                                                            " " +
                                                            (tableData[0]?.[item.currencySymbolCol] || "₺");
                                                    } else {
                                                        cellValue = formatCurrency(total) +  getCurrencySymbolByCurrencyId(props.totalAmountCurrency || 0);
                                                    }
                                                } else {
                                                    cellValue = total;
                                                }
                                            }

                                            return (
                                                <TableCell
                                                    padding="none"
                                                    style={{
                                                        paddingLeft: 10,
                                                        paddingRight: 10
                                                    }}
                                                    className={"whitespace-nowrap !py-2 " + item.bodyClasName}
                                                    key={`total-${item.id}`}
                                                    align={item.isMoney ? "right" : "left"}
                                                    sx={{ fontWeight: "bold", textDecoration: "underline" }}
                                                >
                                                    {cellValue}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow> : null}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
                {isDisablePaging ? null : (
                    <div className="flex flex-row justify-between align-middle px-2 mt-2">
                        <div className="flex flex-row justify-start items-center">
                            <div className="flex flex-row justify-start items-center text-contrast-70 mr-6 text-md">
                                <span className="mr-2">Kayıt Göster</span>
                                <Selector
                                    options={options}
                                    name="sendType"
                                    placement="top"
                                    placeholder={rowsPerPage}
                                    onChange={onLimitChange}

                                />
                            </div>
                            <Pagination currentPage={page} totalCount={totalDataCount != null ? totalDataCount : tableData.length} onPageChange={handleChangePage} pageSize={rowsPerPage}></Pagination>

                        </div>
                        {totalParameter ? <div className="flex items-center text-contrast-70" >
                            {`Toplam ${totalLabel}: ${calculateTotal()}`}
                        </div> : ""}
                        {secondTotalParamater ? <div className="flex items-center text-contrast-70" >
                            {`Toplam ${secondTotalLabel}: ${calculateSecondTotal()}`}
                        </div> : ""}
                        <div className="flex items-center text-contrast-70">
                            Toplam Kayıt: {totalDataCount != null ? totalDataCount : tableData.length}
                        </div>
                    </div>
                )}


            </div>
            {bottomElement ? (
                <div className={`mx-4 p-3 bg-white rounded-md ${disableShadow ? "" : "shadow-md"}`}>
                    {bottomElement}
                </div>
            ) : null}

        </>
    );

})
export default MaterailTable;

const options = [
    {
        value: 10,
        label: "10",
    },
    {
        value: 25,
        label: "25",
    },
    {
        value: 50,
        label: "50",
    },
];